import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import React, { useState } from 'react';
import * as styles from './PostFilterMenu.module.scss';
import Button from '../components/Button';
import Caret from '../components/Caret';
import { MenuItem, MenuSimpleItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';

interface Option {
  icon?: string;
  count?: number;
  label: string;
}

interface OptionWithValue<Value extends string> extends Option {
  value: Value;
}

type OptionsRecord<Type extends string> = Record<Type, Option>;

function optionsRecordToArray<Value extends string>(
  filterOptionsRecord: OptionsRecord<Value>,
): OptionWithValue<Value>[] {
  const entries = Object.entries(filterOptionsRecord) as Array<[Value, Option]>;
  return entries.map(([value, props]) => ({ value, ...props }));
}

interface OptionContentProps {
  icon?: string | null;
  active?: boolean;
  children: React.ReactNode;
}

const OptionContent = ({
  icon = null,
  children,
  active = false,
}: OptionContentProps) => (
  <div className={classNames(styles.option, { [styles.active]: active })}>
    {icon && <SVGIcon>{icon}</SVGIcon>}
    <div className={styles.label}>{children}</div>
  </div>
);

interface PostFilterMenuProps<Value extends string> {
  icon?: string | null;
  menuPlacement?: Placement;
  title?: string;
  options: OptionsRecord<Value>;
  value: Value;
  onChange: (value: Value) => void;
  buttonProps?: React.ComponentProps<typeof Button>;
  showCountInLabel?: boolean;
}

const PostFilterMenu = <Value extends string>({
  icon = null,
  menuPlacement = 'bottom-start',
  title,
  options: optionsRecord,
  value,
  onChange,
  buttonProps,
  showCountInLabel,
}: PostFilterMenuProps<Value>) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = optionsRecordToArray<Value>(optionsRecord);
  const activeOption = options.find((option) => option.value === value);

  return (
    <div className={styles.container}>
      <OverflowMenu
        placement={menuPlacement}
        open={isOpen}
        onOpenChange={setIsOpen}
        button={
          <Button
            className={classNames(styles.button, { [styles.open]: isOpen })}
            {...buttonProps}
          >
            <SVGIcon size={14}>{icon ?? activeOption?.icon}</SVGIcon>
            <div className={styles.label}>
              {showCountInLabel && typeof activeOption?.count === 'number'
                ? `${activeOption.label} · ${activeOption.count}`
                : activeOption?.label}
            </div>
            <Caret direction={isOpen ? 'up' : 'down'} />
          </Button>
        }
      >
        {title && (
          <MenuSimpleItem>
            <h5 className={styles.title}>{title}</h5>
          </MenuSimpleItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => onChange(option.value)}>
            <OptionContent
              icon={option.icon}
              active={activeOption?.value === option.value}
            >
              {typeof option.count === 'number'
                ? `${option.label} · ${option.count}`
                : option.label}
            </OptionContent>
          </MenuItem>
        ))}
      </OverflowMenu>
    </div>
  );
};

export default PostFilterMenu;
